import { useState } from 'react'
import { useParams } from 'react-router-dom'
import type { ScenarioItem } from '../../core/domain/models'
import { SyncStatusIndicator } from '../components/SyncStatusIndicator'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useCommand } from '../hooks/useCommand'
import { useScenarioItems } from '../hooks/useScenarioItems'
import './Scenario.css'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ItemsKindIcon } from '../components/PageKindIcons'
import { PageHeader } from '../components/PageHeader'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'
import { useSmartBack } from '../hooks/useSmartBack'

export default function ScenarioItemsPage() {
    const { scenarioId } = useParams<{ scenarioId: string }>()
    const backToScenario = useSmartBack(scenarioId ? `/scenario/${scenarioId}` : '/')
    const { deleteScenarioItems, addScenarioItems } = useCommand()
    
    // Hook-based data access (Architectural compliance)
    const { items, loading } = useScenarioItems(scenarioId)

    const [sortField, setSortField] = useState<keyof ScenarioItem>('usageCount')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set()) // itemIds
    const [showBulkImport, setShowBulkImport] = useState(false)
    const [importText, setImportText] = useState('')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)

    const toggleSelection = (itemId: string) => {
        const newSelected = new Set(selectedItems)
        if (newSelected.has(itemId)) {
            newSelected.delete(itemId)
        } else {
            newSelected.add(itemId)
        }
        setSelectedItems(newSelected)
    }

    const toggleSelectAll = () => {
        if (selectedItems.size === items.length) {
            setSelectedItems(new Set())
        } else {
            setSelectedItems(new Set(items.map(i => i.id)))
        }
    }
    
    const handleBulkDelete = async () => {
        if (!selectedItems.size || !scenarioId) return
        setShowDeleteConfirm(true)
    }

    const confirmBulkDelete = async () => {
        if (!selectedItems.size || !scenarioId) return
        setShowDeleteConfirm(false)
        
        try {
            const itemIds = Array.from(selectedItems)
            await deleteScenarioItems(scenarioId, itemIds)
            setSelectedItems(new Set())
            setDeleteError(null)
        } catch (error) {
            console.error("Failed to delete items", error)
            setDeleteError("Failed to delete items")
        }
    }

    const handleBulkImport = async () => {
        if (!importText.trim() || !scenarioId) return
        
        try {
            // Parse the textarea: split by newlines, trim each line, filter empty
            const lines = importText
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0)
            
            if (lines.length === 0) return
            
            await addScenarioItems(scenarioId, lines)
            setImportText('')
            setShowBulkImport(false)
        } catch (error) {
            console.error("Failed to import items", error)
        }
    }

    const sortedItems = [...items].sort((a, b) => {
        let valA: any = a[sortField]
        let valB: any = b[sortField]
        
        if (sortField === 'lastUsedAt') {
            valA = new Date(valA).getTime()
            valB = new Date(valB).getTime()
        }
        
        // String comparison for names
        if (typeof valA === 'string' && typeof valB === 'string') {
             valA = valA.toLowerCase()
             valB = valB.toLowerCase()
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1
        return 0
    })

    const handleSort = (field: keyof ScenarioItem) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('desc') 
        }
    }
    
    const renderSortArrow = (field: string) => {
        if (sortField !== field) return null
        return sortDirection === 'asc' ? ' ↑' : ' ↓'
    }

    return (
        <div className="app-container">
            <PageHeader
                kind="Items"
                kindIcon={<ItemsKindIcon />}
                tone="scenario"
                title="All Scenario Items"
                backLabel="Scenario"
                onBack={backToScenario}
                actions={<SyncStatusIndicator />}
            />

            <PwaUpdateBanner />
            
            <main className="app-main meta-items-main" style={{ padding: '2rem' }}>
                {loading && items.length === 0 ? (
                    <LoadingSpinner message="Loading items..." />
                ) : (
                    <div className="card meta-items-card">
                        {selectedItems.size > 0 && (
                            <div className="delete-btn-inline-wrapper">
                                <button 
                                    onClick={handleBulkDelete}
                                    className="delete-btn-inline"
                                >
                                    <span>×</span> 
                                    Delete ({selectedItems.size})
                                </button>
                            </div>
                        )}
                        <div style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
                                    This list contains all unique items ever created in this scenario, ranked by usage.
                                    It combines server data with your pending changes.
                                </p>
                                <button 
                                    onClick={() => setShowBulkImport(!showBulkImport)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: showBulkImport ? '#666' : '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap',
                                        marginLeft: '1rem'
                                    }}
                                >
                                    {showBulkImport ? 'Cancel' : '📥 Bulk Import'}
                                </button>
                            </div>
                            
                            {showBulkImport && (
                                <div style={{ 
                                    marginBottom: '1rem', 
                                    padding: '1rem', 
                                    background: 'rgba(255, 255, 255, 0.05)', 
                                    borderRadius: '4px' 
                                }}>
                                    <textarea
                                        value={importText}
                                        onChange={(e) => setImportText(e.target.value)}
                                        placeholder="Enter one item per line"
                                        style={{
                                            width: '100%',
                                            minHeight: '150px',
                                            padding: '0.75rem',
                                            fontSize: '1rem',
                                            background: '#2a2a2a',
                                            color: 'white',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '4px',
                                            resize: 'vertical'
                                        }}
                                    />
                                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => {
                                                setShowBulkImport(false)
                                                setImportText('')
                                            }}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: '#666',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleBulkImport}
                                            disabled={!importText.trim()}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: importText.trim() ? '#4CAF50' : '#444',
                                                color: importText.trim() ? 'white' : '#888',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: importText.trim() ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            Import
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                                        <th style={{ padding: '12px 8px', width: '40px' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={items.length > 0 && selectedItems.size === items.length}
                                                onChange={toggleSelectAll}
                                                style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                            />
                                        </th>
                                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', padding: '12px 8px' }}>
                                            Item Name {renderSortArrow('name')}
                                        </th>
                                        <th onClick={() => handleSort('usageCount')} style={{ cursor: 'pointer', padding: '12px 8px', textAlign: 'right' }}>
                                            Usage Count {renderSortArrow('usageCount')}
                                        </th>
                                        <th onClick={() => handleSort('lastUsedAt')} style={{ cursor: 'pointer', padding: '12px 8px', textAlign: 'right' }}>
                                            Last Used {renderSortArrow('lastUsedAt')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedItems.length > 0 ? sortedItems.map(item => (
                                        <tr key={item.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: selectedItems.has(item.id) ? 'rgba(255, 255, 255, 0.05)' : 'transparent' }}>
                                            <td style={{ padding: '12px 8px' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedItems.has(item.id)}
                                                    onChange={() => toggleSelection(item.id)}
                                                    style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                                />
                                            </td>
                                            <td style={{ padding: '12px 8px' }}>{item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name}</td>
                                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>{item.usageCount}</td>
                                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>{new Date(item.lastUsedAt).toLocaleDateString()}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
                                                No items found in this scenario yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmBulkDelete}
                title="Delete Items"
                message={`Delete ${selectedItems.size} item(s) permanently?`}
                warningMessage="This action cannot be undone."
                confirmLabel="Delete"
                confirmVariant="danger"
            />

            {deleteError && (
                <div style={{ position: 'fixed', bottom: '1rem', left: '50%', transform: 'translateX(-50%)', background: 'var(--color-danger, #e53e3e)', color: 'white', padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-md, 8px)', fontSize: '0.9rem', zIndex: 1200, boxShadow: 'var(--shadow-lg)' }}>
                    {deleteError}
                    <button onClick={() => setDeleteError(null)} style={{ marginLeft: '1rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Dismiss</button>
                </div>
            )}
        </div>
    )
}